<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'
import * as THREE from 'three'
import earthVertexShader from '@/shaders/earth/vertex.vs?raw'
import earthFragmentShader from '@/shaders/earth/fragment.fs?raw'

import earthTexture from '@/assets/img/earth.jpg'
import glowTexture from '@/assets/img/glow.png'

const canvasRef = ref(null)

let scene, camera, renderer
let earth, earthGlow, stars, atmosphere, borderPoints, dotParticles
let animationId
let continuousRotationY = 0

const createCircleTexture = () => {
  const canvas = document.createElement('canvas')
  canvas.width = 32
  canvas.height = 32
  const ctx = canvas.getContext('2d')

  const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16)
  gradient.addColorStop(0, 'rgba(255,255,255,1)')
  gradient.addColorStop(0.5, 'rgba(255,255,255,0.5)')
  gradient.addColorStop(1, 'rgba(255,255,255,0)')

  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 32, 32)

  return new THREE.CanvasTexture(canvas)
}

const initScene = () => {
  if (!canvasRef.value) return

  scene = new THREE.Scene()

  camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000)
  camera.position.set(0, 0, -150)
  camera.lookAt(0, 0, 0)

  renderer = new THREE.WebGLRenderer({
    canvas: canvasRef.value,
    alpha: true,
    antialias: true,
  })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.setSize(window.innerWidth, window.innerHeight)
}

const createStars = () => {
  const starsGeometry = new THREE.BufferGeometry()
  const starsMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 2,
    transparent: true,
    opacity: 0.8,
    sizeAttenuation: true,
    map: createCircleTexture(),
    blending: THREE.AdditiveBlending,
  })

  const starsVertices = []
  for (let i = 0; i < 4000; i++) {
    starsVertices.push((Math.random() - 0.5) * 2000, (Math.random() - 0.5) * 2000, (Math.random() - 0.5) * 2000)
  }

  starsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starsVertices, 3))
  stars = new THREE.Points(starsGeometry, starsMaterial)
  scene.add(stars)
}

const createEarth = () => {
  const textureLoader = new THREE.TextureLoader()
  const earthGeometry = new THREE.SphereGeometry(100, 64, 64)

  const uniforms = {
    glowColor: { value: new THREE.Color(0x0cd1eb) },
    scale: { value: -1.0 },
    bias: { value: 1.0 },
    power: { value: 3.3 },
    time: { value: 100 },
    map: { value: null },
    ambientLightColor: { value: new THREE.Color(0xffffff) },
    ambientLightIntensity: { value: 1.0 },
  }

  textureLoader.load(earthTexture, (texture) => {
    uniforms.map.value = texture
  })

  const earthMaterial = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: earthVertexShader,
    fragmentShader: earthFragmentShader,
  })

  earth = new THREE.Mesh(earthGeometry, earthMaterial)
  earth.position.set(-100, -50, 0)
  earth.rotation.y = 0.55
  scene.add(earth)

  // Border points
  const borderGeometry = new THREE.SphereGeometry(110, 60, 60)
  const pointMaterial = new THREE.PointsMaterial({
    color: 0x81ffff,
    transparent: true,
    sizeAttenuation: true,
    opacity: 0.1,
    size: 0.5,
  })
  borderPoints = new THREE.Points(borderGeometry, pointMaterial)
  borderPoints.position.copy(earth.position)
  borderPoints.rotation.copy(earth.rotation)
  scene.add(borderPoints)

  // Sprite glow
  const glowTex = textureLoader.load(glowTexture)
  const spriteMaterial = new THREE.SpriteMaterial({
    map: glowTex,
    color: 0x4390d1,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
  })
  earthGlow = new THREE.Sprite(spriteMaterial)
  earthGlow.scale.set(300, 300, 1)
  earthGlow.position.copy(earth.position)
  scene.add(earthGlow)

  // Ambient light
  scene.add(new THREE.AmbientLight(0xffffff, 1.0))
}

const createAtmosphere = () => {
  const atmosphereGeometry = new THREE.SphereGeometry(100, 50, 50)
  const atmosphereMaterial = new THREE.ShaderMaterial({
    uniforms: {
      coeficient: { value: 1.0 },
      power: { value: 3.0 },
      glowColor: { value: new THREE.Color(0x4390d1) },
    },
    vertexShader: `
      varying vec3 vVertexWorldPosition;
      varying vec3 vVertexNormal;
      void main(){
        vVertexNormal = normalize(normalMatrix * normal);
        vVertexWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 glowColor;
      uniform float coeficient;
      uniform float power;
      varying vec3 vVertexNormal;
      varying vec3 vVertexWorldPosition;
      void main(){
        vec3 worldCameraToVertex = vVertexWorldPosition - cameraPosition;
        vec3 viewCameraToVertex = (viewMatrix * vec4(worldCameraToVertex, 0.0)).xyz;
        viewCameraToVertex = normalize(viewCameraToVertex);
        float intensity = pow(coeficient + dot(vVertexNormal, viewCameraToVertex), power);
        gl_FragColor = vec4(glowColor, intensity);
      }
    `,
    blending: THREE.NormalBlending,
    transparent: true,
    depthWrite: false,
  })

  atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial)
  atmosphere.position.copy(earth.position)
  atmosphere.rotation.copy(earth.rotation)
  scene.add(atmosphere)
}

const createDotParticles = () => {
  const dotGeometry = new THREE.BufferGeometry()
  const dotPositions = []

  for (let i = 0; i < 1000; i++) {
    const radius = 106 + Math.random() * 12
    const theta = Math.random() * Math.PI * 2
    const phi = Math.random() * Math.PI
    dotPositions.push(radius * Math.sin(phi) * Math.cos(theta), radius * Math.cos(phi), radius * Math.sin(phi) * Math.sin(theta))
  }

  dotGeometry.setAttribute('position', new THREE.Float32BufferAttribute(dotPositions, 3))

  const dotMaterial = new THREE.PointsMaterial({
    color: 0x00ffff,
    size: 1,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
    map: createCircleTexture(),
  })

  dotParticles = new THREE.Points(dotGeometry, dotMaterial)
  dotParticles.position.copy(earth.position)
  dotParticles.rotation.copy(earth.rotation)
  scene.add(dotParticles)
}

const animate = () => {
  animationId = requestAnimationFrame(animate)

  continuousRotationY += 0.0003

  if (earth) {
    earth.rotation.y = 0.55 + continuousRotationY
  }
  if (borderPoints) {
    borderPoints.rotation.y = earth.rotation.y
  }
  if (atmosphere) {
    atmosphere.rotation.y = earth.rotation.y
  }
  if (dotParticles) {
    dotParticles.rotation.y = earth.rotation.y
  }
  if (stars) {
    stars.rotation.y += 0.0001
  }

  renderer.render(scene, camera)
}

const handleResize = () => {
  if (!camera || !renderer) return
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
}

onMounted(() => {
  initScene()
  createStars()
  createEarth()
  createAtmosphere()
  createDotParticles()
  animate()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  cancelAnimationFrame(animationId)
  if (renderer) renderer.dispose()
})
</script>

<template>
  <canvas ref="canvasRef" class="earth-canvas"></canvas>
</template>

<style scoped>
.earth-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
</style>
